import axios from 'axios';

export default {
  namespaced: true,
  state: {
    currentSurvey: null,
    consumerData: null,
    surveysList: [],
    responsesList: [],
    totalResponses: 0,
    loading: false,
    error: null,
    hasActiveSurvey: false,
    hasVoted: false,
    surveyStatusLoaded: false,
  },
  getters: {
    currentSurvey: (state) => state.currentSurvey,
    consumerData: (state) => state.consumerData,
    surveysList: (state) => state.surveysList,
    responsesList: (state) => state.responsesList,
    totalResponses: (state) => state.totalResponses,
    isLoading: (state) => state.loading,
    surveyError: (state) => state.error,
    hasActiveSurvey: (state) => state.hasActiveSurvey,
    hasVoted: (state) => state.hasVoted,
    surveyStatusLoaded: (state) => state.surveyStatusLoaded,
  },
  mutations: {
    SET_LOADING(state, val) {
      state.loading = val;
    },
    SET_ERROR(state, err) {
      state.error = err;
    },
    SET_CURRENT_SURVEY(state, survey) {
      state.currentSurvey = survey;
    },
    SET_CONSUMER_DATA(state, data) {
      state.consumerData = data;
    },
    SET_SURVEYS_LIST(state, list) {
      state.surveysList = list;
    },
    SET_RESPONSES_LIST(state, { responses, total }) {
      state.responsesList = responses;
      state.totalResponses = total;
    },
    SET_SURVEY_STATUS(state, { hasActiveSurvey, isCompleted, activeSurvey, consumer }) {
      state.hasActiveSurvey = !!hasActiveSurvey;
      state.hasVoted = !!isCompleted;
      state.surveyStatusLoaded = true;
      if (activeSurvey) {
        state.currentSurvey = activeSurvey;
      }
      if (consumer) {
        state.consumerData = consumer;
      }
    },
    CLEAR_SURVEY_STATE(state) {
      state.currentSurvey = null;
      state.consumerData = null;
      state.error = null;
    },
  },
  actions: {
    async verifyEic({ commit }, { eic, accountNumber, contractNumber, surveyType }) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await axios.post('/api/survey/verify-eic', {
          eic,
          accountNumber: accountNumber || contractNumber,
          surveyType,
        });
        commit('SET_CURRENT_SURVEY', response.data.survey);
        commit('SET_CONSUMER_DATA', response.data.consumer);
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        const errMsg = (err.response && err.response.data && err.response.data.error) || 'Помилка перевірки даних споживача';
        commit('SET_ERROR', errMsg);
        throw err;
      }
    },

    async lookupEicByAccount(_, { accountNumber, consumerType = 'household' }) {
      const response = await axios.get('/api/survey/lookup-eic', {
        params: { accountNumber, consumerType },
      });
      return response.data;
    },

    async fetchActiveSurvey({ commit, rootGetters }, consumerType = 'non_household') {
      commit('SET_LOADING', true);
      try {
        const contractNum = rootGetters.contractNumber || '';
        const url = `/api/survey/active?consumerType=${consumerType}&contractNumber=${encodeURIComponent(contractNum)}`;
        const response = await axios.get(url);
        const data = response.data || {};

        commit('SET_SURVEY_STATUS', {
          hasActiveSurvey: data.hasActiveSurvey,
          isCompleted: data.isCompleted,
          activeSurvey: data.activeSurvey,
          consumer: data.consumer,
        });

        if (data.consumer) {
          commit('SET_CONSUMER_DATA', data.consumer);
        } else if (data.userEic) {
          commit('SET_CONSUMER_DATA', {
            eic: data.userEic,
            consumerType: data.consumerType || consumerType,
          });
        }

        commit('SET_LOADING', false);
        return data;
      } catch (err) {
        commit('SET_LOADING', false);
        throw err;
      }
    },

    async fetchUserSurveyStatus({ dispatch }) {
      return await dispatch('fetchActiveSurvey', 'non_household');
    },

    async fetchSurveyById({ commit }, id) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await axios.get(`/api/survey/${id}`);
        commit('SET_CURRENT_SURVEY', response.data);
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        const errMsg = (err.response && err.response.data && err.response.data.error) || 'Помилка завантаження опитування';
        commit('SET_ERROR', errMsg);
        throw err;
      }
    },

    async submitSurvey({ commit }, payload) {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      try {
        const response = await axios.post('/api/survey/submit', payload);
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        const errMsg = (err.response && err.response.data && err.response.data.error) || 'Помилка відправки відповідей';
        commit('SET_ERROR', errMsg);
        throw err;
      }
    },

    async fetchAdminSurveys({ commit }) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.get('/api/survey/admin/list');
        commit('SET_SURVEYS_LIST', response.data);
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        throw err;
      }
    },

    async importSurveyExcel({ commit, dispatch }, formData) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.post('/api/survey/admin/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        await dispatch('fetchAdminSurveys');
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        throw err;
      }
    },

    async updateSurveyStatus({ commit, dispatch }, { id, data }) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.put(`/api/survey/admin/${id}/status`, data);
        await dispatch('fetchAdminSurveys');
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        throw err;
      }
    },

    async deleteSurvey({ commit, dispatch }, id) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.delete(`/api/survey/admin/${id}`);
        await dispatch('fetchAdminSurveys');
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        throw err;
      }
    },

    async fetchAdminResponses({ commit }, params) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.get('/api/survey/admin/responses', { params });
        commit('SET_RESPONSES_LIST', {
          responses: response.data.responses || [],
          total: response.data.total || 0,
        });
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        throw err;
      }
    },

    async deleteSurveyResponse({ commit }, id) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.delete(`/api/survey/admin/responses/${id}`);
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        throw err;
      }
    },

    async seedSampleSurveys({ commit, dispatch }) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.post('/api/survey/admin/seed-samples');
        await dispatch('fetchAdminSurveys');
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        throw err;
      }
    },

    async importConsumersExcel({ commit }, formData) {
      commit('SET_LOADING', true);
      try {
        const response = await axios.post('/api/survey/admin/consumers/import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        commit('SET_LOADING', false);
        return response.data;
      } catch (err) {
        commit('SET_LOADING', false);
        throw err;
      }
    },

    async fetchConsumersStats() {
      try {
        const response = await axios.get('/api/survey/admin/consumers/stats');
        return response.data;
      } catch (err) {
        console.error('Error fetching consumer stats:', err);
        return { total: 0, household: 0, nonHousehold: 0, votedCount: 0 };
      }
    },
  },
};
